// Money-amount formatting for the admin money-ops surfaces.
//
// Operators act on real funds (Pay / Sweep / Treasury withdraw / Refund), so a
// raw integer like `59700000` must NEVER stand alone — it is too easy to
// misread by an order of magnitude. Every money value is rendered as a
// human amount ("59.70 USDC") PLUS its exact raw integer with thousands
// separators ("59,700,000 base units"), so the operator can sanity-check both.
//
// Pure, dependency-free, deterministic. No rounding surprises: the human value
// is computed with integer/BigInt math (not floats).

const GROUPER = new Intl.NumberFormat("en-US");

/** Group an integer string/bigint with thousands separators (e.g. 59700000 →
 *  "59,700,000"). */
export function groupInteger(value: bigint | number | string): string {
  const v = toBigInt(value);
  return GROUPER.format(v);
}

function toBigInt(value: bigint | number | string): bigint {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("formatAmount: non-finite number");
    return BigInt(Math.trunc(value));
  }
  const s = value.trim();
  return BigInt(s === "" ? "0" : s);
}

/** Convert an integer minor/base-unit value to a fixed-decimals human string,
 *  with thousands separators on the integer part. Integer math only.
 *  e.g. formatUnits(59700000, 6) → "59.70" ; formatUnits(5970, 2) → "59.70". */
export function formatUnits(value: bigint | number | string, decimals: number): string {
  if (!Number.isInteger(decimals) || decimals < 0) throw new Error("formatAmount: bad decimals");
  const v = toBigInt(value);
  const neg = v < 0n;
  const abs = neg ? -v : v;
  const base = 10n ** BigInt(decimals);
  const intPart = abs / base;
  const fracPart = abs % base;
  const intStr = GROUPER.format(intPart);
  const fracStr = decimals > 0 ? "." + fracPart.toString().padStart(decimals, "0") : "";
  return (neg ? "-" : "") + intStr + fracStr;
}

export interface MoneyAmount {
  /** Fixed-decimals human number, e.g. "59.70" (no symbol). */
  human: string;
  /** Token symbol, e.g. "USDC". */
  symbol: string;
  decimals: number;
  /** "59.70 USDC" — the primary, must-show display. */
  display: string;
  /** "59,700,000 base units" — exact raw integer with separators. */
  raw: string;
}

/**
 * Build the canonical money rendering from a raw integer amount + token meta.
 *
 * @param baseUnits   integer minor/base-unit amount (number|bigint|string)
 * @param decimals    token decimals (USDC=6; USD-cents minor=2)
 * @param symbol      token symbol ("USDC", "USD")
 * @param rawUnitLabel label for the raw integer ("base units" | "minor")
 */
export function formatTokenAmount(
  baseUnits: bigint | number | string,
  decimals: number,
  symbol: string,
  rawUnitLabel = "base units",
): MoneyAmount {
  const human = formatUnits(baseUnits, decimals);
  return {
    human,
    symbol,
    decimals,
    display: `${human} ${symbol}`,
    raw: `${groupInteger(baseUnits)} ${rawUnitLabel}`,
  };
}
